import { selectProjectIntegrations } from "@/store/integrations/integrations-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import {
  SphereXGIcon,
  type FileDetails,
  type Option,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { BcfServicesIntegrationType } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useMemo, type JSX, type PropsWithChildren } from "react";
import { BetaBadge } from "../beta-badge";
import { AccAnnotation } from "./acc-annotation";
import { AnnotationCreationData } from "./annotation-props";
import {
  AnnotationTypeOption,
  AnnotationTypeOptionHeader,
} from "./annotation-type-option";
import { ExternalAnnotation } from "./external-annotation";
import { ExternalAnnotationProviderLogo } from "./external-annotation-provider-logo";
import { getAllBcfServicesIntegrationTypes } from "./external-annotation-utils";
import { ProcoreAnnotation } from "./procore-annotation";
import { SphereAnnotation } from "./sphere-annotation";

export interface CreateAnnotationFormProps {
  /** Callback called when the dialog is closed (successfully or non-successfully). use to clear the annotationToCreate. */
  onClose(): void;

  /** Callback called when the user click the save button */
  onSave(details: AnnotationCreationData): void;

  /** If true, it will allow the creation of external annotations if there are some integrations enabled */
  allowExternalMarkups?: boolean;

  /**
   * Optional initial value for the annotation title.
   */
  initialTitle?: string;

  /**
   * Optional initial value for the annotation description.
   */
  initialDescription?: string;

  /**
   * Optional attachment to seed the attachments list with when the dialog
   * opens. The user can preview and remove it before saving, just like a
   * file they had picked themselves.
   *
   * The attachment is expected to carry `urlOrFile: File` (i.e. a not-yet-
   * uploaded file) so it flows through the form's existing upload-on-save
   * path.
   */
  initialAttachment?: FileDetails;
}

/**
  If there are less or equal annotation type options than this minimum number, we will not show the dropdown.
  The value is 1 instead of 0 because the sphere annotation is always available and is already added to the annotation type options
 */
const MINIMUM_ANNOTATION_TYPE_OPTIONS = 1;

/** List of annotation type currently supported */
enum AnnotationOptionType {
  FaroSphereXg = "FARO Sphere XG",
  ProcoreRfi = "Procore RFI",
  ProcoreObservation = "Procore Observation",
  AccRfi = "ACC RFI",
  AccIssue = "ACC Issue",
  AutodeskBim360Rfi = "Autodesk BIM360 RFI",
  AutodeskBim360Issue = "Autodesk BIM360 Issue",
}

/**
 * @param value The string value of the AnnotationOptionType enum
 * @returns The AnnotationOptionType enum key corresponding to the given input value string
 */
function getCorrespondingAnnotationType(value: string): AnnotationOptionType {
  switch (value) {
    case AnnotationOptionType.FaroSphereXg:
      return AnnotationOptionType.FaroSphereXg;
    case AnnotationOptionType.ProcoreRfi:
      return AnnotationOptionType.ProcoreRfi;
    case AnnotationOptionType.ProcoreObservation:
      return AnnotationOptionType.ProcoreObservation;
    case AnnotationOptionType.AccRfi:
      return AnnotationOptionType.AccRfi;
    case AnnotationOptionType.AccIssue:
      return AnnotationOptionType.AccIssue;
    case AnnotationOptionType.AutodeskBim360Rfi:
      return AnnotationOptionType.AutodeskBim360Rfi;
    case AnnotationOptionType.AutodeskBim360Issue:
      return AnnotationOptionType.AutodeskBim360Issue;
    default:
      throw new Error("Invalid annotation option type value");
  }
}

/** Stores all the dropdown option data for each BCF Services integration type */
const externalAnnotationTypesOptions: Record<
  BcfServicesIntegrationType,
  Option
> = {
  [BcfServicesIntegrationType.procoreRfis]: {
    key: AnnotationOptionType.ProcoreRfi,
    value: AnnotationOptionType.ProcoreRfi,
    label: (
      <AnnotationTypeOption
        label={AnnotationOptionType.ProcoreRfi}
        logo={
          <ExternalAnnotationProviderLogo
            annotationType={BcfServicesIntegrationType.procoreRfis}
            ariaLabel="Logo Procore RFI"
          />
        }
      />
    ),
  },
  [BcfServicesIntegrationType.procoreObservations]: {
    key: AnnotationOptionType.ProcoreObservation,
    value: AnnotationOptionType.ProcoreObservation,
    label: (
      <AnnotationTypeOption
        label={AnnotationOptionType.ProcoreObservation}
        logo={
          <ExternalAnnotationProviderLogo
            annotationType={BcfServicesIntegrationType.procoreObservations}
            ariaLabel="Logo Procore Observation"
          />
        }
      />
    ),
  },
  [BcfServicesIntegrationType.autodeskAccRfis]: {
    key: AnnotationOptionType.AccRfi,
    value: AnnotationOptionType.AccRfi,
    label: (
      <AnnotationTypeOption
        label={AnnotationOptionType.AccRfi}
        logo={
          <ExternalAnnotationProviderLogo
            annotationType={BcfServicesIntegrationType.autodeskAccRfis}
            ariaLabel="Logo Autodesk ACC RFI"
          />
        }
      />
    ),
  },
  [BcfServicesIntegrationType.autodeskAccIssues]: {
    key: AnnotationOptionType.AccIssue,
    value: AnnotationOptionType.AccIssue,
    label: (
      <AnnotationTypeOption
        label={AnnotationOptionType.AccIssue}
        logo={
          <ExternalAnnotationProviderLogo
            annotationType={BcfServicesIntegrationType.autodeskAccIssues}
            ariaLabel="Logo Autodesk ACC Issue"
          />
        }
      />
    ),
  },
  [BcfServicesIntegrationType.autodeskBim360Rfis]: {
    key: AnnotationOptionType.AutodeskBim360Rfi,
    value: AnnotationOptionType.AutodeskBim360Rfi,
    label: (
      <AnnotationTypeOption
        label={AnnotationOptionType.AutodeskBim360Rfi}
        logo={
          <ExternalAnnotationProviderLogo
            annotationType={BcfServicesIntegrationType.autodeskBim360Rfis}
            ariaLabel="Logo Autodesk BIM360 RFI"
          />
        }
      />
    ),
  },
  [BcfServicesIntegrationType.autodeskBim360Issues]: {
    key: AnnotationOptionType.AutodeskBim360Issue,
    value: AnnotationOptionType.AutodeskBim360Issue,
    label: (
      <AnnotationTypeOption
        label={AnnotationOptionType.AutodeskBim360Issue}
        logo={
          <ExternalAnnotationProviderLogo
            annotationType={BcfServicesIntegrationType.autodeskBim360Issues}
            ariaLabel="Logo Autodesk BIM360 issue"
          />
        }
      />
    ),
  },
};

/**
 *
 * @param annotationType The annotation option type
 * @returns The corresponding BCF Services integration type
 */
function getCorrespondingBcfIntegrationType(
  annotationType: AnnotationOptionType,
): BcfServicesIntegrationType {
  for (const [key, value] of Object.entries(externalAnnotationTypesOptions)) {
    if (value.value === annotationType) {
      // This type cast below is safe because of definition of externalAnnotationTypesOptions (Record< BcfServicesIntegrationType, Option>)
      // Using externalAnnotationTypesOptions to find the corresponding BCF Service integration type has the benefit to avoid any
      // modification of this function in the future contrary to a switch case. In addition, the code is shorter and simpler.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return key as BcfServicesIntegrationType;
    }
  }
  throw new Error("Invalid annotation option type value");
}

/**
 * @param valueToCheck The value to verify
 * @param expectedValues The expected values
 * @returns The verified value if it matches one of the expected values otherwise throws an error
 */
function validateBcfIntegrationType<T extends BcfServicesIntegrationType[]>(
  valueToCheck: BcfServicesIntegrationType,
  ...expectedValues: T
): T[number] {
  assert(
    expectedValues.includes(valueToCheck),
    `The value to check ${valueToCheck} is not one of the expected values: ${expectedValues}`,
  );
  return valueToCheck;
}

/** @returns The tooltip used for the external annotations AnnotationTypeOptionHeader */
function IntegrationHeaderTooltip(): JSX.Element {
  return (
    <Stack direction="column">
      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: "center",
          justifyContent: "flex-start",
          marginBottom: 1,
        }}
      >
        <b>Integrations</b>
        <BetaBadge dark />
      </Stack>
      Create annotations connected with third-party services for seamless
      tracking and coordination across tools.
    </Stack>
  );
}

/** @returns The label used for the external annotations AnnotationTypeOptionHeader */
function IntegrationHeaderLabel(): JSX.Element {
  return (
    <Stack
      direction="row"
      sx={{ gap: 1, alignItems: "center", justifyContent: "flex-start" }}
    >
      INTEGRATIONS
      <BetaBadge dark />
    </Stack>
  );
}

/**
 * @returns A form to persist an annotation to the project
 */
export function CreateAnnotationForm({
  children,
  onClose,
  onSave,
  allowExternalMarkups = false,
  initialTitle,
  initialDescription,
  initialAttachment,
}: PropsWithChildren<CreateAnnotationFormProps>): JSX.Element {
  const [persistedAnnotationType, setPersistedAnnotationType] =
    useLocalStorage<AnnotationOptionType>(
      "annotationType",
      AnnotationOptionType.FaroSphereXg,
    );

  const projectIntegrations = useAppSelector(
    selectProjectIntegrations,
    isEqual,
  );

  const annotationTypeOptions: Option[] = useMemo(() => {
    const sphereAnnotation: Option = {
      key: AnnotationOptionType.FaroSphereXg,
      value: AnnotationOptionType.FaroSphereXg,
      label: (
        <AnnotationTypeOption
          label={AnnotationOptionType.FaroSphereXg}
          logo={<SphereXGIcon aria-label="Logo Sphere XG" />}
        />
      ),
    };

    const integrationsAnnotations = projectIntegrations
      .map((integration) => getAllBcfServicesIntegrationTypes(integration))
      .flat()
      .map(
        (bcfIntegrationType) =>
          externalAnnotationTypesOptions[bcfIntegrationType],
      );

    const integrationsHeader: Option[] =
      integrationsAnnotations.length > 0
        ? [
            {
              key: "integrationsHeader",
              value: "Integrations",
              label: (
                <AnnotationTypeOptionHeader
                  label={<IntegrationHeaderLabel />}
                  tooltipText={<IntegrationHeaderTooltip />}
                />
              ),
              isHeader: true,
            },
          ]
        : [];
    return [
      sphereAnnotation,
      ...integrationsHeader,
      ...integrationsAnnotations,
    ];
  }, [projectIntegrations]);

  // The persisted annotation type in local storage may not be available to select in the current project.
  // In this case, we will fallback to the default annotation type (FARO Sphere XG)
  const annotationType = useMemo(
    () =>
      annotationTypeOptions.some(
        (option) =>
          !option.isHeader && option.value === persistedAnnotationType,
      )
        ? persistedAnnotationType
        : AnnotationOptionType.FaroSphereXg,
    [annotationTypeOptions, persistedAnnotationType],
  );

  const enableExternalMarkups =
    annotationTypeOptions.length > MINIMUM_ANNOTATION_TYPE_OPTIONS &&
    allowExternalMarkups;

  if (
    !enableExternalMarkups ||
    annotationType === AnnotationOptionType.FaroSphereXg
  ) {
    return (
      <SphereAnnotation
        onClose={onClose}
        onSave={onSave}
        showAnnotationTypeDropdown={enableExternalMarkups}
        selectedAnnotationType={annotationType}
        onAnnotationTypeChange={(value) =>
          setPersistedAnnotationType(getCorrespondingAnnotationType(value))
        }
        annotationTypeOptions={annotationTypeOptions}
        initialTitle={initialTitle}
        initialDescription={initialDescription}
        initialAttachment={initialAttachment}
      >
        {children}
      </SphereAnnotation>
    );
  } else if (
    annotationType === AnnotationOptionType.ProcoreRfi ||
    annotationType === AnnotationOptionType.ProcoreObservation
  ) {
    return (
      <ProcoreAnnotation
        onClose={onClose}
        onSave={onSave}
        selectedAnnotationType={annotationType}
        integrationType={validateBcfIntegrationType(
          getCorrespondingBcfIntegrationType(annotationType),
          BcfServicesIntegrationType.procoreObservations,
          BcfServicesIntegrationType.procoreRfis,
        )}
        onAnnotationTypeChange={(value) =>
          setPersistedAnnotationType(getCorrespondingAnnotationType(value))
        }
        annotationTypeOptions={annotationTypeOptions}
      />
    );
  } else if (
    annotationType === AnnotationOptionType.AccRfi ||
    annotationType === AnnotationOptionType.AccIssue
  ) {
    return (
      <AccAnnotation
        onClose={onClose}
        onSave={onSave}
        selectedAnnotationType={annotationType}
        integrationType={validateBcfIntegrationType(
          getCorrespondingBcfIntegrationType(annotationType),
          BcfServicesIntegrationType.autodeskAccIssues,
          BcfServicesIntegrationType.autodeskAccRfis,
        )}
        onAnnotationTypeChange={(value) =>
          setPersistedAnnotationType(getCorrespondingAnnotationType(value))
        }
        annotationTypeOptions={annotationTypeOptions}
      />
    );
  }
  return (
    <ExternalAnnotation
      onClose={onClose}
      onSave={onSave}
      selectedAnnotationType={annotationType}
      onAnnotationTypeChange={(value) =>
        setPersistedAnnotationType(getCorrespondingAnnotationType(value))
      }
      annotationTypeOptions={annotationTypeOptions}
    />
  );
}
